import { placedDecimal, roundToPlaces, type UnitsRounding } from './scaled';

const COMPACT_SUFFIXES = ['', 'K', 'M', 'B', 'T'] as const;

/**
 * Base units as a compact figure with up to `maximumFractionDigits` places
 * and no trailing zeros: "850K", "1.23M". The unit is picked after
 * rounding, so 999,999.6 reads "1M", never "1000K". The bigint counterpart
 * of `formatCompactNumber` (`Intl` compact notation), rounded half up on
 * the exact amount (or as `round` says).
 */
export function formatCompactUnits(
  raw: bigint,
  decimals: number,
  {
    maximumFractionDigits = 1,
    round = 'half-up'
  }: { maximumFractionDigits?: number; round?: UnitsRounding } = {}
): string {
  if (raw < 0n) {
    return `-${formatCompactUnits(-raw, decimals, { maximumFractionDigits, round })}`;
  }
  const largest = COMPACT_SUFFIXES.length - 1;
  const whole = (raw / 10n ** BigInt(decimals)).toString();
  let step =
    whole === '0' ? 0 : Math.min(Math.floor((whole.length - 1) / 3), largest);
  const limit = 1_000n * 10n ** BigInt(maximumFractionDigits);
  for (;;) {
    const scaled = roundToPlaces(
      raw,
      decimals + step * 3,
      maximumFractionDigits,
      round
    );
    if (step < largest && scaled >= limit) {
      step += 1;
      continue;
    }
    // Compact figures are not grouped below ten thousand of their unit.
    const figure = placedDecimal(scaled, maximumFractionDigits, 0);
    return `${scaled < 10_000n * 10n ** BigInt(maximumFractionDigits) ? figure.replace(/,/g, '') : figure}${COMPACT_SUFFIXES[step]}`;
  }
}
